import { AddRounded as AddRoundedIcon, ArrowBack as ArrowBackIcon, DeleteOutline as DeleteOutlineIcon } from "@mui/icons-material";
import { Button, IconButton, MenuItem, TextField } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getLocale } from "../../../../i18n";
import useNoti from "../../../../hooks/useNoti";
import { useTranslation } from "../../../../i18n/useTranslation";
import { AppNotifications } from "../../../../models";
import { FeedbackService } from "../../../../services/feedback.service";
import { MESSAGES } from "../../../constants/mock";
import Loading from "../../../components/Loading";
import "./index.scss";

const emptyRow = () => "";

const STATUS_OPTIONS = ["open", "in_progress", "resolved", "closed"] as const;

type FeedbackAttachment = {
  url?: string | null;
  kind?: string | null;
  mimeType?: string | null;
  size?: number | null;
};

type FeedbackTimelineItem = {
  status?: string | null;
  statusLabel?: string | null;
  note?: string | null;
  at?: string | null;
  byType?: string;
  byName?: string | null;
  attachments?: FeedbackAttachment[];
};

type FeedbackLogItem = {
  id: string;
  ticketCode?: string;
  topic: string;
  topicLabel: string;
  message: string;
  status?: string;
  statusLabel?: string;
  attachments?: FeedbackAttachment[];
  timeline?: FeedbackTimelineItem[];
  mailSent: boolean;
  createdAt: string;
  updatedAt?: string;
  closedAt?: string | null;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(getLocale() === "en" ? "en-US" : "vi-VN", {
    timeZone: "Asia/Ho_Chi_Minh",
    hour12: false,
  });
};

const truncateMessage = (value?: string, max = 120) => {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (text.length <= max) return text || "-";
  return `${text.slice(0, max).trim()}…`;
};

const PROGRESS_MAX_FILES = 5;
const PROGRESS_MAX_FILE_BYTES = 25 * 1024 * 1024;

type DraftProgressFile = {
  id: string;
  file: File;
  previewUrl: string;
};

const FeedbackMail: React.FC = () => {
  const { t } = useTranslation();
  const [pushNoti] = useNoti();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toEmails, setToEmails] = useState<string[]>([emptyRow()]);
  const [ccEmails, setCcEmails] = useState<string[]>([emptyRow()]);
  const [logs, setLogs] = useState<FeedbackLogItem[]>([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [logsTotal, setLogsTotal] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draftStatus, setDraftStatus] = useState<string>("open");
  const [draftNote, setDraftNote] = useState("");
  const [draftFiles, setDraftFiles] = useState<DraftProgressFile[]>([]);
  const [updating, setUpdating] = useState(false);

  const selectedTicket = useMemo(
    () => logs.find((item) => item.id === selectedId) || null,
    [logs, selectedId],
  );

  const clearDraftFiles = useCallback(() => {
    setDraftFiles((prev) => {
      prev.forEach((entry) => URL.revokeObjectURL(entry.previewUrl));
      return [];
    });
  }, []);

  useEffect(() => () => {
    draftFiles.forEach((entry) => URL.revokeObjectURL(entry.previewUrl));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const res = await FeedbackService.getMailConfig();
      const data = res.data?.data || {};
      setToEmails(data.to?.length ? data.to : [emptyRow()]);
      setCcEmails(data.cc?.length ? data.cc : [emptyRow()]);
    } catch (error) {
      console.error(error);
      pushNoti(AppNotifications.ERROR, MESSAGES.ERROR);
    } finally {
      setLoading(false);
    }
  }, [pushNoti]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const fetchLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      const res = await FeedbackService.listLogs({ page: 1, limit: 50 });
      setLogs(res.data?.data?.items || []);
      setLogsTotal(res.data?.data?.pagination?.total || 0);
    } catch (error) {
      console.error(error);
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const updateRow = (
    list: string[],
    setter: (next: string[]) => void,
    index: number,
    value: string,
  ) => {
    const next = [...list];
    next[index] = value;
    setter(next);
  };

  const addRow = (list: string[], setter: (next: string[]) => void) => {
    setter([...list, emptyRow()]);
  };

  const removeRow = (list: string[], setter: (next: string[]) => void, index: number) => {
    const next = list.filter((_, idx) => idx !== index);
    setter(next.length ? next : [emptyRow()]);
  };

  const save = async () => {
    try {
      setSaving(true);
      const to = toEmails.map((email) => email.trim()).filter(Boolean);
      const cc = ccEmails.map((email) => email.trim()).filter(Boolean);
      const res = await FeedbackService.updateMailConfig({ to, cc });
      if (res.status === 200 || res.status === 201) {
        const data = res.data?.data || {};
        setToEmails(data.to?.length ? data.to : [emptyRow()]);
        setCcEmails(data.cc?.length ? data.cc : [emptyRow()]);
        pushNoti(AppNotifications.SUCCESS, MESSAGES.SUCCESS);
      } else {
        pushNoti(AppNotifications.ERROR, res.data?.message || MESSAGES.ERROR);
      }
    } catch (error) {
      pushNoti(AppNotifications.ERROR, MESSAGES.ERROR);
    } finally {
      setSaving(false);
    }
  };

  const openTicket = (item: FeedbackLogItem) => {
    clearDraftFiles();
    setSelectedId(item.id);
    setDraftStatus(item.status || "open");
    setDraftNote("");
  };

  const closeDetail = () => {
    clearDraftFiles();
    setSelectedId(null);
    setDraftNote("");
  };

  const addDraftFiles = (picked: FileList | null) => {
    if (!picked?.length) return;
    const next: DraftProgressFile[] = [];
    let errorKey: string | null = null;
    Array.from(picked).forEach((file) => {
      if (errorKey) return;
      if (!String(file.type || "").startsWith("image/")) {
        errorKey = "progressFileType";
        return;
      }
      if (file.size > PROGRESS_MAX_FILE_BYTES) {
        errorKey = "progressFileTooLarge";
        return;
      }
      if (draftFiles.length + next.length >= PROGRESS_MAX_FILES) {
        errorKey = "progressFileTooMany";
        return;
      }
      next.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        previewUrl: URL.createObjectURL(file),
      });
    });
    if (errorKey) {
      pushNoti(AppNotifications.ERROR, t(`feedback.${errorKey}`));
    }
    if (next.length) {
      setDraftFiles((prev) => [...prev, ...next]);
    }
  };

  const removeDraftFile = (id: string) => {
    setDraftFiles((prev) => {
      const target = prev.find((entry) => entry.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((entry) => entry.id !== id);
    });
  };

  const saveTicketStatus = async (status = draftStatus, note = draftNote) => {
    if (!selectedTicket) return;
    try {
      setUpdating(true);
      const formData = new FormData();
      formData.append("status", status);
      const trimmedNote = note.trim();
      if (trimmedNote) formData.append("note", trimmedNote);
      draftFiles.forEach((entry) => formData.append("files", entry.file));

      const res = await FeedbackService.updateLog(selectedTicket.id, formData);
      if (res.status === 200 || res.status === 201) {
        const updated = res.data?.data;
        setLogs((prev) =>
          prev.map((row) =>
            row.id === selectedTicket.id ? { ...row, ...updated, id: selectedTicket.id } : row,
          ),
        );
        setDraftStatus(updated?.status || status);
        setDraftNote("");
        clearDraftFiles();
        pushNoti(AppNotifications.SUCCESS, t("feedback.statusUpdated"));
      } else {
        pushNoti(AppNotifications.ERROR, res.data?.message || MESSAGES.ERROR);
      }
    } catch (error) {
      pushNoti(AppNotifications.ERROR, MESSAGES.ERROR);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  const renderEmailList = (
    title: string,
    hint: string,
    list: string[],
    setter: (next: string[]) => void,
  ) => (
    <div className="feedback-mail__group">
      <div className="feedback-mail__group-head">
        <div>
          <h4>{title}</h4>
          <p>{hint}</p>
        </div>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddRoundedIcon />}
          onClick={() => addRow(list, setter)}
        >
          {t("feedback.addEmail")}
        </Button>
      </div>
      {list.map((email, index) => (
        <div className="feedback-mail__row" key={`${title}-${index}`}>
          <TextField
            fullWidth
            type="email"
            size="small"
            placeholder="name@company.com"
            value={email}
            onChange={(event) => updateRow(list, setter, index, event.target.value)}
          />
          <IconButton
            aria-label={t("common.delete")}
            onClick={() => removeRow(list, setter, index)}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </div>
      ))}
    </div>
  );

  if (selectedTicket) {
    const attachments = Array.isArray(selectedTicket.attachments)
      ? selectedTicket.attachments
      : [];
    return (
      <div className="feedback-mail feedback-mail--detail">
        <div className="feedback-mail__detail-top">
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={closeDetail}
          >
            {t("feedback.backToList")}
          </Button>
          <Button variant="outlined" size="small" onClick={fetchLogs} disabled={logsLoading}>
            {t("common.refresh")}
          </Button>
        </div>

        <div className="feedback-mail__detail-card">
          <div className="feedback-mail__detail-header">
            <div>
              <div className="feedback-mail__detail-label">{t("feedback.logTicket")}</div>
              <div className="feedback-mail__detail-code">
                {selectedTicket.ticketCode || "-"}
              </div>
            </div>
            <div className="feedback-mail__detail-status">
              {selectedTicket.statusLabel
                || t(`feedback.status.${selectedTicket.status || "open"}`)}
            </div>
          </div>

          <div className="feedback-mail__detail-meta">
            <div>
              <span>{t("feedback.logTopic")}</span>
              <strong>{selectedTicket.topicLabel || selectedTicket.topic}</strong>
            </div>
            <div>
              <span>{t("feedback.logTime")}</span>
              <strong>{formatDateTime(selectedTicket.createdAt)}</strong>
            </div>
            <div>
              <span>{t("feedback.logMail")}</span>
              <strong>
                {selectedTicket.mailSent ? t("feedback.mailSent") : t("feedback.mailNotSent")}
              </strong>
            </div>
          </div>

          <div className="feedback-mail__detail-section">
            <h4>{t("feedback.logMessage")}</h4>
            <pre className="feedback-mail__detail-message">{selectedTicket.message}</pre>
          </div>

          {attachments.length > 0 ? (
            <div className="feedback-mail__detail-section">
              <h4>{t("feedback.attachments", { count: attachments.length })}</h4>
              <ul className="feedback-mail__attachments">
                {attachments.map((item, index) => (
                  <li key={`${selectedTicket.id}-file-${index}`}>
                    <a href={item.url || "#"} target="_blank" rel="noreferrer">
                      {t("feedback.attachmentItem", {
                        index: index + 1,
                        kind: item.kind === "video"
                          ? t("feedback.attachmentVideo")
                          : t("feedback.attachmentImage"),
                      })}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="feedback-mail__detail-grid">
            <div className="feedback-mail__detail-section">
              <h4>{t("feedback.timeline")}</h4>
              {(selectedTicket.timeline || []).length === 0 ? (
                <p className="feedback-mail__muted">{t("feedback.timelineEmpty")}</p>
              ) : (
                <ul className="feedback-mail__timeline-list">
                  {(selectedTicket.timeline || []).map((entry, index) => (
                    <li key={`${selectedTicket.id}-tl-${index}`}>
                      <div className="feedback-mail__timeline-head">
                        <strong>
                          {entry.statusLabel
                            || entry.byName
                            || entry.byType
                            || t("feedback.timeline")}
                        </strong>
                        <span>{formatDateTime(entry.at)}</span>
                      </div>
                      {entry.byName || entry.byType ? (
                        <div className="feedback-mail__muted">
                          {entry.byName || entry.byType}
                        </div>
                      ) : null}
                      {entry.note ? <p>{entry.note}</p> : null}
                      {(entry.attachments || []).length > 0 ? (
                        <ul className="feedback-mail__timeline-media">
                          {(entry.attachments || []).map((file, fileIndex) => (
                            <li key={`${selectedTicket.id}-tl-${index}-file-${fileIndex}`}>
                              <a href={file.url || "#"} target="_blank" rel="noreferrer">
                                {file.kind === "image" && file.url ? (
                                  <img src={file.url} alt="" />
                                ) : (
                                  t("feedback.attachmentItem", {
                                    index: fileIndex + 1,
                                    kind: file.kind === "video"
                                      ? t("feedback.attachmentVideo")
                                      : t("feedback.attachmentImage"),
                                  })
                                )}
                              </a>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="feedback-mail__detail-section feedback-mail__detail-actions">
              <h4>{t("feedback.handleTicket")}</h4>
              <TextField
                select
                size="small"
                fullWidth
                label={t("feedback.logStatus")}
                value={draftStatus}
                onChange={(event) => setDraftStatus(event.target.value)}
              >
                {STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status}>
                    {t(`feedback.status.${status}`)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={3}
                label={t("feedback.statusNote")}
                value={draftNote}
                onChange={(event) => setDraftNote(event.target.value)}
              />
              <div className="feedback-mail__progress-upload">
                <label className="feedback-mail__progress-label" htmlFor="feedback-progress-files">
                  {t("feedback.progressImages")}
                </label>
                <p className="feedback-mail__muted">{t("feedback.progressImagesHint")}</p>
                <input
                  id="feedback-progress-files"
                  type="file"
                  accept="image/*"
                  multiple
                  disabled={updating}
                  onChange={(event) => {
                    addDraftFiles(event.target.files);
                    event.target.value = "";
                  }}
                />
                {draftFiles.length > 0 ? (
                  <ul className="feedback-mail__progress-preview">
                    {draftFiles.map((entry) => (
                      <li key={entry.id}>
                        <img src={entry.previewUrl} alt="" />
                        <button
                          type="button"
                          onClick={() => removeDraftFile(entry.id)}
                          disabled={updating}
                        >
                          {t("feedback.removeImage")}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
              <div className="feedback-mail__update-actions">
                <Button
                  variant="outlined"
                  disabled={updating || selectedTicket.status === "closed"}
                  onClick={() => saveTicketStatus("closed", draftNote || t("feedback.closedByAdmin"))}
                >
                  {t("feedback.closeTicket")}
                </Button>
                <Button
                  variant="contained"
                  disabled={updating}
                  onClick={() => saveTicketStatus()}
                >
                  {updating ? t("feedback.updating") : t("feedback.updateStatus")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="feedback-mail">
      <div className="feedback-mail__header">
        <div>
          <h3>{t("feedback.title")}</h3>
          <p>{t("feedback.hint")}</p>
        </div>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? t("feedback.saving") : t("common.save")}
        </Button>
      </div>
      {renderEmailList(t("feedback.to"), t("feedback.toHint"), toEmails, setToEmails)}
      {renderEmailList(t("feedback.cc"), t("feedback.ccHint"), ccEmails, setCcEmails)}

      <div className="feedback-mail__logs">
        <div className="feedback-mail__logs-head">
          <div>
            <h4>{t("feedback.logsTitle")}</h4>
            <p>{t("feedback.logsHint", { count: logsTotal })}</p>
          </div>
          <Button variant="outlined" size="small" onClick={fetchLogs} disabled={logsLoading}>
            {t("common.refresh")}
          </Button>
        </div>
        <div className="feedback-mail__table">
          <div className="feedback-mail__table-row feedback-mail__table-row--head">
            <div>{t("feedback.logTime")}</div>
            <div>{t("feedback.logTicket")}</div>
            <div>{t("feedback.logTopic")}</div>
            <div>{t("feedback.logStatus")}</div>
            <div>{t("feedback.logMessage")}</div>
            <div>{t("feedback.logMail")}</div>
          </div>
          {logsLoading ? (
            <div className="feedback-mail__empty">{t("feedback.logsLoading")}</div>
          ) : logs.length === 0 ? (
            <div className="feedback-mail__empty">{t("feedback.logsEmpty")}</div>
          ) : (
            logs.map((item) => (
              <div className="feedback-mail__table-row" key={item.id}>
                <div>{formatDateTime(item.createdAt)}</div>
                <div className="feedback-mail__ticket">
                  <button type="button" onClick={() => openTicket(item)}>
                    {item.ticketCode || "-"}
                  </button>
                </div>
                <div>{item.topicLabel || item.topic}</div>
                <div>{item.statusLabel || item.status || "-"}</div>
                <div className="feedback-mail__message">{truncateMessage(item.message)}</div>
                <div>
                  {item.mailSent ? t("feedback.mailSent") : t("feedback.mailNotSent")}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackMail;
